import { useMeeting, useParticipant } from "@videosdk.live/react-sdk";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import ReactPlayer from "react-player";
import "./css/ParticipantView.css";
import MicOffWhite from "./icons/MicOffWhite.svg";
import MicOnWhite from "./icons/MicOnWhite.svg";
import { useMeetingAppContext } from "../MeetingAppContextDef";
import RaisedHand from "./icons/Studentlist/RaisedHand";


export const CornerDisplayName = ({
  participantId,
  isPresenting,
  displayName,
  isLocal,
  micOn,
}) => {

  const {
    webcamStream,
    micStream,
    getVideoStats,
    getAudioStats,
    getShareStats,
  } = useParticipant(participantId);

  const statsIntervalIdRef = useRef();
  const [score, setScore] = useState({});
  const [audioStats, setAudioStats] = useState({});
  const [videoStats, setVideoStats] = useState({});

  const updateStats = async () => {
    let stats = [];
    let audioStats = [];
    let videoStats = [];
    if (isPresenting) {
      stats = await getShareStats();
    } else if (webcamStream) {
      stats = await getVideoStats();
    } else if (micStream) {
      stats = await getAudioStats();
    }

    if (webcamStream || micStream || isPresenting) {
      videoStats = isPresenting ? await getShareStats() : await getVideoStats();
      audioStats = isPresenting ? [] : await getAudioStats();
    }

    let score = stats ? (stats.length > 0 ? 100 : 100) : 100;

    setScore(score);
    setAudioStats(audioStats);
    setVideoStats(videoStats);
  };


  useEffect(() => {
    if (webcamStream || micStream) {
      updateStats();

      if (statsIntervalIdRef.current) {
        clearInterval(statsIntervalIdRef.current);
      }

      statsIntervalIdRef.current = setInterval(updateStats, 1000);
    } else {
      if (statsIntervalIdRef.current) {
        clearInterval(statsIntervalIdRef.current);
        statsIntervalIdRef.current = null;
      }
    }

    return () => {
      if (statsIntervalIdRef.current) clearInterval(statsIntervalIdRef.current);
    };
  }, [webcamStream, micStream]);


  return (
    <>
      <div className="VideoBottomBar VideoBottomBarMd">
        <div className="VideoBottomBarLeft">
          <div className="MicIcon">
            {micOn ? (
              <img src={MicOnWhite} alt="MicOnWhite" />
            ) : (
              <img src={MicOffWhite} alt="MicOffWhite" />
            )}
          </div>

          <div className="parcipateName">
            {isPresenting
              ? isLocal
                ? "You are presenting"
                : `${displayName} is presenting`
              : isLocal
                ? "You"
                : displayName}
          </div>
        </div>
        
        {/* Add fullscreen button if more than one participant */}
       
      </div>
    </>
  );
};

export default function ParticipantView({ participantId, hiddenVideo = false, setFullscreenParticipantId, fullscreenParticipantId ,onWebcamStatusChange}) {
  const mMeeting = useMeeting();
  const participants = mMeeting?.participants;
  const targetRef = useRef(null);

  const {
    displayName,
    webcamStream,
    micStream,
    webcamOn,
    micOn,
    isLocal,
    isActiveSpeaker,
    setQuality,
    metaData
  } = useParticipant(participantId, {
    onStreamEnabled,
  });
  
  const { raisedHandsParticipants } = useMeetingAppContext();
  const [isRaisedHand, setIsRaiseHand] = useState(false);
  
  useEffect(() => {
    const hasRaisedHand = raisedHandsParticipants?.some(
      participant => participant.participantId === participantId
    );
    
    setIsRaiseHand(hasRaisedHand || false);
  }, [raisedHandsParticipants, participantId]);

  function onStreamEnabled(stream) {
    if (stream.kind === 'audio') {
      // Audio Stream On
    }
  }


  useEffect(() => {
    if (participants.length > 1) {
      if(participantId.includes('teacher')){
       setQuality('high')
      } else{
       setQuality("high");

      }
    }
  }, [participants.length]);
  
  const micRef = useRef(null);
  const [mouseOver, setMouseOver] = useState(false);
  const isMobile = false;

  useEffect(() => {
    if (onWebcamStatusChange) {
      onWebcamStatusChange(participantId, webcamOn);
    }
  }, [webcamOn, participantId, onWebcamStatusChange]);

  useEffect(() => {
    if (micRef.current) {
      if (micOn && micStream) {
        const mediaStream = new MediaStream();
        mediaStream.addTrack(micStream.track);
        micRef.current.srcObject = mediaStream;
        micRef.current
          .play()
          .catch((error) =>
            console.error("videoElem.current.play() failed", error)
          );
      } else {
        micRef.current.srcObject = null;
      }
    }
  }, [micStream, micOn]);

  const webcamMediaStream = useMemo(() => {
    if (webcamOn && webcamStream && !hiddenVideo) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(webcamStream.track);
      return mediaStream;
    }
  }, [webcamStream, webcamOn, hiddenVideo]);

  const handlePauseStream = () => {
    if (webcamStream) {
      webcamStream.pause();
    }
  };

  const handleResumeStream = () => {
    if (webcamStream) {
      webcamStream.resume();
    }
  };

  const getInitials = (name) => {
    if (!name) return '';
    
    const nameParts = name.split(' ');
    
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0);
    }
    
    return nameParts[0].charAt(0) + nameParts[1].charAt(0);
  };

  useEffect(() => {
    let observer;
    if (webcamMediaStream && isMobile) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            handleResumeStream();
          } else {
            handlePauseStream();
          }
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0.5,
        }
      );

      if (targetRef.current) {
        observer.observe(targetRef.current);
      }
    }

    return () => {
      if (targetRef.current && observer) {
        observer.unobserve(targetRef.current);
      }
    };
  }, [webcamMediaStream]);


  const avatar = metaData?.avatar;
  const isValidAvatarString = typeof avatar === 'string' && avatar.trim() !== '';
  const showInitial = !isValidAvatarString || (isValidAvatarString && avatar.includes("user_place_holder"));

  const avatarContent = showInitial ? (
    getInitials(displayName)
  ) : (
    <img
      src={avatar}
      alt="avatar"
      className="w-full h-full object-cover rounded-full"
    />
  );

  return (
    <>
      <div
        ref={targetRef}
        onMouseEnter={() => {
          setMouseOver(true);
        }}
        style ={fullscreenParticipantId && participantId!=fullscreenParticipantId?{display:"none"}:{}}
        onMouseLeave={() => {
          setMouseOver(false);
        }}
        className={`h-full w-full bg-gray-750 relative overflow-hidden rounded-lg video-cover ${isActiveSpeaker && micOn ? "ActiveMic" : "NoneActiveMic"} ${(participants.size > 25) ? "MaxParticipant" : ""}`}
      >
        {isRaisedHand && (
          <div style={{
            position: 'absolute',
            top: '0.5rem',
            left: '0.5rem',
            zIndex: 5
          }}>
            <RaisedHand width="32px" height="35px" />
          </div>
        )}
        
        <audio ref={micRef} autoPlay muted={isLocal} />
        
        {!hiddenVideo ? (
          webcamOn ? (
            <ReactPlayer
              playsinline
              playIcon={<></>}
              pip={false}
              light={false}
              controls={false}
              muted={true}
              playing={true}
              url={webcamMediaStream}
              height={"100%"}
              width={"100%"}
              onError={(err) => {
                console.log("participant video error", err);
              }}
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center relative">
              <div className="none-video-cover">
                <div className="aspect-video w-24 h-24 flex items-center justify-center">
                  {isActiveSpeaker && micOn ? (
                    <div className="owalBox RippleEffect">{avatarContent}</div>
                  ) : (
                    <div className="owalBox">{avatarContent}</div>
                  )}
                </div>
              </div>
            </div>
          )
        ) : null}
        
        <CornerDisplayName
          isLocal={isLocal}
          displayName={displayName}
          micOn={micOn}
          webcamOn={webcamOn}
          isPresenting={false}
          participantId={participantId}
          mouseOver={mouseOver}
          isActiveSpeaker={isActiveSpeaker}
          participants={participants} // Pass participants to check count
        />
      </div>

    </>
  );
}